import type { ItemType, ProductType } from "@jasper/client-models";
import type { useOrderConfiguration } from "@jasper/use-cases";

export type OrderConfiguration = ReturnType<typeof useOrderConfiguration>;

export type ConfigurableProduct = NonNullable<
  NonNullable<OrderConfiguration["order"]>["products"]
>[number];

/**
 * Lets a teeth step pick the product type a teeth group needs, for steps whose
 * products change type with the selection (a crown on several teeth is splinted).
 */
export type TeethMutationStrategy = {
  getTargetProductType: (teeth: number[]) => ProductType;
  canReuseProduct?: (args: {
    currentProductType: ProductType;
    targetProductType: ProductType;
  }) => boolean;
};

export type StepSaveState = {
  isDirty: boolean;
  isInvalid: boolean;
  isSaving: boolean;
  isValidating: boolean;
  flush: () => Promise<void>;
  validateAllFields: () => Promise<void>;
  stepKey: StepKeys;
};

export type RegisterStepSave = (
  stepKey: StepKeys,
  getState: () => StepSaveState
) => () => void;

/**
 * props shared by every product configuration step.
 *
 * Steps are host-agnostic views: navigation (`onNext`/`onBack`) and the loaded
 * product/order configuration are injected by the host (webapp multi-product
 * flow via useOrderWorkflow, labs single-product flow via useProductWorkflow).
 *
 * `products` are the products the step configures, the one the user is on
 * first. Every step saves all of them with `updateProducts`. The teeth and arch
 * steps can also create and delete products, one per selected teeth group.
 */
export type ProductStepProps = {
  orderId: string;
  stepKey?: StepKeys;
  itemType: ItemType;
  products: ConfigurableProduct[];
  orderConfiguration: OrderConfiguration;
  registerStepSave?: RegisterStepSave;
  goToStep?: () => void;
};

export type StepKeys =
  | "material"
  | "inlayCore"
  | "preferences"
  | "adjacentTeeth"
  | "implantBridgeTeeth"
  | "teeth"
  | "tooth"
  | "crownTeeth"
  | "arch"
  | "waxUpType"
  | "implantConnection"
  | "abutment"
  | "implantRetention"
  | "inlayCoreCrown"
  | "splintType";

export const StepKeysEnum = {
  adjacentTeeth: "adjacentTeeth",
  teeth: "teeth",
  tooth: "tooth",
  crownTeeth: "crownTeeth",
  implantBridgeTeeth: "implantBridgeTeeth",
  arch: "arch",
  material: "material",
  inlayCore: "inlayCore",
  preferences: "preferences",
  waxUpType: "waxUpType",
  implantConnection: "implantConnection",
  abutment: "abutment",
  implantRetention: "implantRetention",
  inlayCoreCrown: "inlayCoreCrown",
  splintType: "splintType",
} as const satisfies Record<string, StepKeys>;
